import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Button, Skeleton } from 'antd';

import GroupHeader from '../components/common/GroupHeader';
import { confirmDestructive } from '../components/common/confirmDestructive';
import { COLLECTION_ICON, PLUS } from '../components/icons';
import AddToDashboardModal from '../components/reports/AddToDashboardModal';
import ChartRow, { chartPath } from '../components/reports/ChartRow';
import ChartSuggestions from '../components/reports/ChartSuggestions';
import { SUGGESTION_LIMITS, suggestCharts, suggestionCards } from '../components/chartBuilder/suggestions';
import DashboardRow from '../components/reports/DashboardRow';
import NameChartModal from '../components/reports/NameChartModal';
import NameDashboardModal from '../components/reports/NameDashboardModal';
import { chartActions, deleteChartCopy } from '../components/reports/chartActions';
import { absoluteUrl, dashboardActions, deleteDashboardCopy } from '../components/reports/dashboardActions';
import useChartShare, { chartElements } from '../components/reports/useChartShare';
import { useCollectionContext } from '../contexts/CollectionContext';
import { useOptionalCardDefinitions } from '../contexts/CardDefinitionsContext';
import useCharts from '../hooks/useCharts';
import useDashboards from '../hooks/useDashboards';
import { CHART_SCOPE } from '../services/charts';
import { DASHBOARD_SCOPE } from '../services/dashboards';
import useChartPlacements from '../hooks/useChartPlacements';
import { copyTextToClipboard } from '../utils/clipboard';
import { getCollectionFamilyCardIds } from '../utils/collections';
import { hasSubCollections as collectionHasSubs } from '../utils/collectionFamily';
import { dashboardPath, newDashboardPath } from '../utils/dashboards';
import { normalizeId } from '../utils/ids';
import { canManage, canReadCharts } from '../utils/permissions';

/** Where a new chart is made: the chart builder (charts A5b). */
export const newChartPath = (collectionId) => `/collections/${collectionId}/charts/new`;
export const editChartPath = (collectionId, chartId) => `/collections/${collectionId}/charts/${chartId}/edit`;

/**
 * Reports — `/collections/:collectionId/reports` (plan §6.1; the owner's
 * ruling q03: Reports sits in the collection's own bar). The collection page
 * where its dashboards and charts are listed and managed.
 *
 * Drawn under the collection's own bar (its route carries the layout's
 * handle, config/routeHandles), so there is never a second header. The body is the platform's list grammar:
 * two `GroupHeader` sections — **Dashboards** with **New dashboard** as its
 * action (charts A6), then **Charts** with **New chart** — and the rows by
 * tier within each: **Pinned for everyone** (a manager's), then **Yours**.
 * A sub-collection lists only its own: it inherits no dashboard or chart from
 * the collections above it (owner ruling 2026-10-21, KPR-1084). Each row is
 * text (DashboardRow, ChartRow), never a live chart.
 *
 * Charts are for people who read the collection themselves: a global
 * collection's consumer and a link-only reader are offered none (ruled d14;
 * the API answers them 404), so the page says so instead of asking.
 *
 * SUGGESTED CHARTS (charts A10): starting points made from the collection's
 * cards by the capability table (chartBuilder/suggestions) — six under the
 * empty state ("No charts yet. Start from a suggestion or choose New chart."),
 * three after the charts once there are some, never one the collection already
 * shows. Text, like every row here: nothing runs until one is chosen, and then
 * the builder opens with it filled in. ⚑ Offered to everyone Reports is
 * offered to: anyone who reads the collection may save a chart of their own,
 * so no reader here is shown a starting point they cannot keep.
 */
export default function ReportsPage() {
	const { collection, resolvedCollectionId, collectionError } = useCollectionContext() || {};
	const navigate = useNavigate();
	const { modal, message } = App.useApp();
	const collectionId = resolvedCollectionId;
	const allowed = Boolean(collection) && canReadCharts(collection);
	const isManager = Boolean(collection) && canManage(collection);
	const { charts, loading, error, reload, rename, duplicate, remove, promote, demote } = useCharts(collectionId, { enabled: allowed });
	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = useMemo(() => cardDefinitions?.cardsById || {}, [cardDefinitions?.cardsById]);
	const noteMissingCards = cardDefinitions?.noteMissingCards;
	const cards = useMemo(() => (Array.isArray(cardDefinitions?.cards) ? cardDefinitions.cards : []), [cardDefinitions?.cards]);
	const getElementsForCard = cardDefinitions?.getElementsForCard;
	const cardsLoading = Boolean(cardDefinitions?.loading);
	// { mode: 'rename' | 'duplicate', chart } while the naming window is open.
	const [naming, setNaming] = useState(null);
	const boards = useDashboards(collectionId, { enabled: allowed });
	// { mode: 'rename' | 'duplicate', dashboard } while its naming window is open.
	const [namingBoard, setNamingBoard] = useState(null);
	// The chart being placed on a dashboard (Add to dashboard…).
	const [placing, setPlacing] = useState(null);
	// Pin to Home, Show on my Overview, Show on Overview for everyone (A7).
	const placements = useChartPlacements(collectionId);
	// Share & export (A9): a row has no chart drawn, so Download image runs it
	// first; Download data runs it at the export grade; Copy link is its page.
	const share = useChartShare({ collectionId });

	// A chart's card the cache has not seen (one added since) is fetched, so
	// its row can wear the card's badge.
	useEffect(() => {
		if (typeof noteMissingCards !== 'function') return;
		const missing = charts.map((c) => normalizeId(c?.spec?.card_id)).filter((id) => id && !cardsById[id]);
		if (missing.length) noteMissingCards(missing);
	}, [charts, cardsById, noteMissingCards]);

	const forEveryone = charts.filter((c) => c?.scope === CHART_SCOPE.COLLECTION);
	const yours = charts.filter((c) => c?.scope !== CHART_SCOPE.COLLECTION);
	// The suggestions: made from the cards once they are loaded, never run.
	const suggestions = useMemo(() => {
		if (!allowed || !collection || cardsLoading || loading || error) return [];
		const familyIds = getCollectionFamilyCardIds(collection, cards);
		return suggestCharts({
			cards: suggestionCards(collection, cards, familyIds),
			getElementsForCard,
			allCards: cards,
			familyIds,
			hasSubCollections: collectionHasSubs(collection),
			existing: charts.map((c) => c?.spec),
			limit: charts.length ? SUGGESTION_LIMITS.strip : SUGGESTION_LIMITS.empty,
		});
	}, [allowed, collection, cardsLoading, loading, error, cards, getElementsForCard, charts]);

	const boardsForEveryone = boards.dashboards.filter((d) => d?.scope === DASHBOARD_SCOPE.COLLECTION);
	const boardsYours = boards.dashboards.filter((d) => d?.scope !== DASHBOARD_SCOPE.COLLECTION);

	const confirmDelete = (chart) => {
		const copy = deleteChartCopy(chart);
		confirmDestructive(modal, { ...copy, okText: 'Delete', onOk: () => remove(chart) });
	};
	const confirmDeleteBoard = (dashboard) => {
		const copy = deleteDashboardCopy(dashboard);
		confirmDestructive(modal, { ...copy, okText: 'Delete', onOk: () => boards.remove(dashboard) });
	};
	const copyBoardLink = async (dashboard) => {
		const ok = await copyTextToClipboard(absoluteUrl(dashboardPath(collectionId, normalizeId(dashboard?._id))));
		if (ok) message.success('Link copied to clipboard');
		else message.error('Could not copy the link.');
	};

	const rowFor = (chart) => {
		const id = normalizeId(chart?._id);
		const cardId = normalizeId(chart?.spec?.card_id);
		const actions = chartActions({
			chart,
			isManager,
			onOpen: () => navigate(chartPath(collectionId, id)),
			onEdit: () => navigate(editChartPath(collectionId, id)),
			onRename: () => setNaming({ mode: 'rename', chart }),
			onDuplicate: () => setNaming({ mode: 'duplicate', chart }),
			onAddToDashboard: () => setPlacing(chart),
			...placements.chart(chart),
			onPromote: () => promote(chart),
			onDemote: () => demote(chart),
			onDownloadImage: () => share.downloadImage(chart, { elements: chartElements(chart, getElementsForCard, { cardsById, elementSetsById: cardDefinitions?.elementSetsById }) }),
			imageReason: share.busy === 'image' ? 'Downloading the image…' : null,
			onDownloadData: () => share.downloadData(chart, { cardName: cardId ? cardsById[cardId]?.name : undefined, elements: chartElements(chart, getElementsForCard, { cardsById, elementSetsById: cardDefinitions?.elementSetsById }) }),
			dataReason: share.busy === 'data' ? 'Downloading the data…' : null,
			onCopyLink: () => share.copyLink(chart),
			linkReason: share.busy === 'link' ? 'Copying the link…' : null,
			onDelete: () => confirmDelete(chart),
		});
		return <ChartRow key={id} chart={chart} collectionId={collectionId} card={cardId ? cardsById[cardId] : null} actions={actions} />;
	};

	const boardRowFor = (dashboard) => {
		const id = normalizeId(dashboard?._id);
		const actions = dashboardActions({
			dashboard,
			isManager,
			onOpen: () => navigate(dashboardPath(collectionId, id)),
			onRename: () => setNamingBoard({ mode: 'rename', dashboard }),
			onDuplicate: () => setNamingBoard({ mode: 'duplicate', dashboard }),
			...placements.dashboard(dashboard),
			onPromote: () => boards.promote(dashboard),
			onDemote: () => boards.demote(dashboard),
			onCopyLink: () => copyBoardLink(dashboard),
			onDelete: () => confirmDeleteBoard(dashboard),
		});
		return <DashboardRow key={id} dashboard={dashboard} collectionId={collectionId} actions={actions} />;
	};

	const tier = (key, label, list, renderRow, { glyph = null } = {}) => (list.length ? (
		<div className='reports-tier' key={key} data-tier={key}>
			<div className='micro-label reports-tier__label'>
				{glyph}
				{label}
			</div>
			<ul className='reports-list'>{list.map(renderRow)}</ul>
		</div>
	) : null);
	const pinnedGlyph = <COLLECTION_ICON aria-hidden='true' className='reports-tier__glyph' />;

	if (collection && !allowed) {
		return (
			<div className='reports-page'>
				<p className='reports-page__empty'>Charts are for people who read this collection themselves, so there are none to show here.</p>
			</div>
		);
	}

	// One section's body: what it holds by tier, or why it holds nothing.
	const sectionBody = ({ total, isLoading, failed, onRetry, failedText, emptyText, tiers, emptyExtra = null }) => {
		if (!collection && collectionError) return null; // The collection's own bar says why.
		if (!collection || (isLoading && !total)) return <Skeleton active paragraph={{ rows: 3 }} title={false} className='reports-page__loading' />;
		if (failed) {
			return (
				<div className='reports-page__error' role='alert'>
					<span>{failedText}</span>
					<Button size='small' onClick={onRetry}>Try again</Button>
				</div>
			);
		}
		if (!total) {
			return (
				<>
					<p className='reports-page__empty'>{emptyText}</p>
					{emptyExtra}
				</>
			);
		}
		return tiers;
	};

	const boardTotal = boards.dashboards.length;
	const total = charts.length;

	return (
		<div className='reports-page'>
			<section className='reports-page__section' aria-label='Dashboards'>
				<GroupHeader
					label='Dashboards'
					count={boardTotal}
					countLabel='dashboard'
					action={collectionId ? (
						<Button type='text' size='small' icon={<PLUS />} className='reports-page__new' onClick={() => navigate(newDashboardPath(collectionId))}>
							New dashboard
						</Button>
					) : null}
				/>
				{sectionBody({
					total: boardTotal,
					isLoading: boards.loading,
					failed: Boolean(boards.error),
					onRetry: boards.reload,
					failedText: "Dashboards couldn't be loaded. Trying again may help.",
					emptyText: 'No dashboards yet. Choose New dashboard to make one.',
					tiers: (
						<>
							{tier('collection', 'Pinned for everyone', boardsForEveryone, boardRowFor, { glyph: pinnedGlyph })}
							{tier('user', 'Yours', boardsYours, boardRowFor)}
						</>
					),
				})}
			</section>

			<section className='reports-page__section' aria-label='Charts'>
				<GroupHeader
					label='Charts'
					count={total}
					countLabel='chart'
					action={collectionId ? (
						<Button type='text' size='small' icon={<PLUS />} className='reports-page__new' onClick={() => navigate(newChartPath(collectionId))}>
							New chart
						</Button>
					) : null}
				/>
				{sectionBody({
					total,
					isLoading: loading,
					failed: Boolean(error),
					onRetry: reload,
					failedText: "Charts couldn't be loaded. Trying again may help.",
					emptyText: suggestions.length ? 'No charts yet. Start from a suggestion or choose New chart.' : 'No charts yet. Choose New chart to make one.',
					emptyExtra: <ChartSuggestions suggestions={suggestions} collectionId={collectionId} />,
					tiers: (
						<>
							{tier('collection', 'Pinned for everyone', forEveryone, rowFor, { glyph: pinnedGlyph })}
							{tier('user', 'Yours', yours, rowFor)}
							<ChartSuggestions suggestions={suggestions} collectionId={collectionId} />
						</>
					),
				})}
			</section>

			{share.modal}
			<NameChartModal
				open={Boolean(naming)}
				mode={naming?.mode}
				chart={naming?.chart}
				onClose={() => setNaming(null)}
				onSubmit={(values) => (naming?.mode === 'duplicate' ? duplicate(naming.chart, values) : rename(naming.chart, values))}
			/>
			<AddToDashboardModal
				open={Boolean(placing)}
				chart={placing}
				collectionId={collectionId}
				onPlaced={() => boards.reload()}
				onClose={() => setPlacing(null)}
			/>
			<NameDashboardModal
				open={Boolean(namingBoard)}
				mode={namingBoard?.mode}
				dashboard={namingBoard?.dashboard}
				onClose={() => setNamingBoard(null)}
				onSubmit={async (values) => {
					if (namingBoard?.mode === 'duplicate') {
						const created = await boards.duplicate(namingBoard.dashboard, values);
						message.success(`Made "${created?.name || values.name}", a copy only you see.`);
						return;
					}
					await boards.rename(namingBoard.dashboard, values);
				}}
			/>
		</div>
	);
}
